import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import messages from '../../messagesOuigoRefunds';
import VouchersTableState from './VouchersTableState';
import inlineStyles, {
	BodyCellText,
	HeaderContentButton,
	SelectionCheckbox,
	TableHeaderActions,
} from './styles';

const getBaseColumns = (showFinalizedDateColumn) => {
	const columns = [
		{
			key: 'supplyChannelRecordLocator',
			label: messages.lblSupplyChannelRecordLocator,
			width: showFinalizedDateColumn ? '24%' : '31%',
		},
		{
			key: 'bookingLocator',
			label: messages.lblBookingLocator,
			width: showFinalizedDateColumn ? '16%' : '18%',
		},
		{
			key: 'voucherNumber',
			label: messages.lblVoucherNumber,
			width: showFinalizedDateColumn ? '16%' : '18%',
		},
		{
			key: 'issueDate',
			label: messages.lblIssueDate,
			width: showFinalizedDateColumn ? '14%' : '16%',
		},
	];

	if (showFinalizedDateColumn) {
		columns.push({
			key: 'finalizedDate',
			label: messages.lblFinalisedDate,
			width: '14%',
		});
	}

	return [
		...columns,
		{
			key: 'value',
			label: messages.lblValue,
			width: showFinalizedDateColumn ? '16%' : '17%',
		},
	];
};

const getSelectableColumns = (baseColumns) => [
	{ ...baseColumns[0], width: '25%' },
	...baseColumns.slice(1),
	{
		key: 'selection',
		width: '6%',
	},
];

const getSortIconStyle = (column, sortColumn) => ({
	...inlineStyles.sortIcon,
	...(column.key === sortColumn ? inlineStyles.activeSortIcon : {}),
});

const VouchersTable = ({
	ariaLabel = 'OuiGo refund vouchers with issue dates and values',
	hasError = false,
	headerAction = null,
	isSelectable = false,
	isLoading = false,
	onCurrentPageSelectionChange = () => {},
	onSortChange = () => {},
	onVoucherSelectionChange = () => {},
	selectedVoucherIds = [],
	showFinalizedDateColumn = false,
	sortColumn = null,
	vouchers,
}) => {
	const { formatMessage } = useIntl();
	const baseColumns = getBaseColumns(showFinalizedDateColumn);
	const columns = isSelectable
		? getSelectableColumns(baseColumns)
		: baseColumns;
	const currentPageVoucherIds = vouchers.map((voucher) => voucher.id);
	const selectedCurrentPageCount = currentPageVoucherIds.filter((voucherId) =>
		selectedVoucherIds.includes(voucherId),
	).length;
	const areAllCurrentPageVouchersSelected =
		currentPageVoucherIds.length > 0 &&
		selectedCurrentPageCount === currentPageVoucherIds.length;
	const isSelectionDisabled =
		isLoading || hasError || currentPageVoucherIds.length === 0;

	return (
		<TableContainer style={inlineStyles.tableContainer}>
			{headerAction && <TableHeaderActions>{headerAction}</TableHeaderActions>}
			<Table style={inlineStyles.table} aria-label={ariaLabel}>
				<TableHead>
					<TableRow style={inlineStyles.headerRow}>
						{columns.map((column) => {
							if (column.key === 'selection') {
								return (
									<TableCell
										key={column.key}
										style={{
											...inlineStyles.headerCell,
											width: column.width,
										}}
									>
										<SelectionCheckbox
											checked={areAllCurrentPageVouchersSelected}
											disabled={isSelectionDisabled}
											label={formatMessage(
												areAllCurrentPageVouchersSelected
													? messages.lblClearAll
													: messages.lblSelectAll,
											)}
											labelPosition="left"
											name="select-all-available-vouchers"
											onChange={() =>
												onCurrentPageSelectionChange(
													!areAllCurrentPageVouchersSelected,
												)
											}
											variant="neutral2"
										/>
									</TableCell>
								);
							}

							return (
								<TableCell
									key={column.key}
									style={{
										...inlineStyles.headerCell,
										width: column.width,
									}}
								>
									<HeaderContentButton
										aria-label="Sort"
										disabled={isLoading || hasError}
										onClick={() => onSortChange(column.key)}
										type="button"
									>
										<FormattedMessage {...column.label} />
										<SwapVertIcon
											aria-hidden="true"
											focusable="false"
											style={getSortIconStyle(column, sortColumn)}
										/>
									</HeaderContentButton>
								</TableCell>
							);
						})}
					</TableRow>
				</TableHead>
				<TableBody>
					{isLoading && (
						<VouchersTableState columns={columns} type="loadingRows" />
					)}
					{!isLoading && hasError && (
						<VouchersTableState columns={columns} type="error" />
					)}
					{!isLoading && !hasError && vouchers.length === 0 && (
						<VouchersTableState columns={columns} type="empty" />
					)}
					{!isLoading &&
						!hasError &&
						vouchers.map((voucher) => {
							const isVoucherSelected = selectedVoucherIds.includes(voucher.id);

							return (
								<TableRow key={voucher.id} style={inlineStyles.bodyRow}>
									{columns.map((column, columnIndex) => {
										const isFirstCell = columnIndex === 0;
										const isLastCell = columnIndex === columns.length - 1;
										const cellStyle = {
											...inlineStyles.rowCellBase,
											...inlineStyles.bodyCell,
											...(isFirstCell ? inlineStyles.firstRowCell : {}),
											...(isLastCell ? inlineStyles.lastRowCell : {}),
										};

										return (
											<TableCell key={column.key} style={cellStyle}>
												{column.key === 'selection' ? (
													<SelectionCheckbox
														checked={isVoucherSelected}
														inputProps={{
															'aria-label': formatMessage(
																isVoucherSelected
																	? messages.lblDeselectVoucher
																	: messages.lblSelectVoucher,
																{ voucherNumber: voucher.voucherNumber },
															),
														}}
														name={`select-voucher-${voucher.id}`}
														onChange={(isSelected) =>
															onVoucherSelectionChange(voucher.id, isSelected)
														}
														variant="neutral2"
													/>
												) : (
													<BodyCellText>{voucher[column.key]}</BodyCellText>
												)}
											</TableCell>
										);
									})}
								</TableRow>
							);
						})}
				</TableBody>
			</Table>
			{isLoading && (
				<VouchersTableState columns={columns} type="loadingSpinner" />
			)}
		</TableContainer>
	);
};

VouchersTable.propTypes = {
	ariaLabel: PropTypes.string,
	hasError: PropTypes.bool,
	headerAction: PropTypes.node,
	isSelectable: PropTypes.bool,
	isLoading: PropTypes.bool,
	onCurrentPageSelectionChange: PropTypes.func,
	onSortChange: PropTypes.func,
	onVoucherSelectionChange: PropTypes.func,
	selectedVoucherIds: PropTypes.arrayOf(PropTypes.string),
	showFinalizedDateColumn: PropTypes.bool,
	sortColumn: PropTypes.string,
	vouchers: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			supplyChannelRecordLocator: PropTypes.string.isRequired,
			bookingLocator: PropTypes.string.isRequired,
			voucherNumber: PropTypes.string.isRequired,
			issueDate: PropTypes.string,
			finalizedDate: PropTypes.string,
			value: PropTypes.string.isRequired,
		}),
	).isRequired,
};

export default VouchersTable;
